/**
 * Theme Colors Loader
 *
 * Fetches admin-customised theme palettes (light + dark) from the backend
 * and injects them as a <style id="ccuk-theme-colors"> tag that overrides
 * the compiled CSS variables in index.css.
 *
 * Caches in localStorage so the palette applies on first paint on subsequent
 * loads (no FOUC). The cache is refreshed from the network on every mount.
 */
const API_URL = window.__BACKEND_URL__;
const CACHE_KEY = 'ccuk_theme_colors';
const STYLE_TAG_ID = 'ccuk-theme-colors';

import { safeSetItem } from './safeStorage';

const PRIMARY_DARK_FOREGROUND = '#0B0D12';
const PRIMARY_LIGHT_FOREGROUND = '#FFFFFF';

// Dark-mode refinement, September 2026. Only migrate a palette when it still
// resembles the old stock CCUK dark palette. This preserves deliberately
// customised admin palettes and bundled presets such as Cinematic Dark.
const DARK_PALETTE_MIGRATIONS = {
  '--theme-bg': { from: ['#0B0C0E'], to: '#0B0D12' },
  '--theme-bg-secondary': { from: ['#101114'], to: '#10131A' },
  '--theme-app-header': { from: ['#0B0C0E'], to: '#10131A' },
  '--theme-app-sidebar': { from: ['#0B1120'], to: '#0F131B' },
  '--theme-app-body': { from: ['#0B0C0E'], to: '#0B0D12' },
  '--theme-surface': { from: ['#15161A'], to: '#131720' },
  '--theme-border': { from: ['#26282F', '#3D3F47'], to: '#282E3A' },
  '--theme-dashboard-card': { from: ['#111827'], to: '#131720' },
  '--theme-dashboard-card-elevated': { from: ['#1A1D26'], to: '#181D28' },
  '--theme-dashboard-card-border': { from: ['#263142'], to: '#2B3442' },
  '--theme-dashboard-card-hover': { from: ['#182033'], to: '#1A2230' },
  '--theme-text': { from: ['#F3F4F7'], to: '#F4F6FA' },
  '--theme-text-muted': { from: ['#93969F'], to: '#A8B0BF' },
  '--theme-text-heading': { from: ['#F3F4F7'], to: '#F4F6FA' },
  '--theme-text-link': { from: ['#6A8CFF'], to: '#7AA2FF' },
  '--theme-icon': { from: ['#F3F4F7'], to: '#F4F6FA' },
  '--theme-primary-hover': { from: ['#0055FF'], to: '#3B6FFF' },
};

const LEGACY_ORANGE_PRIMARY = new Set([
  '#CC730C',
  '#F59E0B',
  '#D97706',
  '#EA580C',
  '#F97316',
  '#FB923C',
]);

const LEGACY_STOCK_SIGNATURE_TOKENS = [
  '--theme-bg',
  '--theme-bg-secondary',
  '--theme-app-header',
  '--theme-app-sidebar',
  '--theme-app-body',
  '--theme-surface',
  '--theme-border',
  '--theme-dashboard-card',
  '--theme-dashboard-card-elevated',
  '--theme-dashboard-card-border',
  '--theme-dashboard-card-hover',
  '--theme-text',
  '--theme-text-muted',
  '--theme-text-heading',
  '--theme-text-link',
  '--theme-icon',
  '--theme-primary-hover',
];

function normaliseHex(value) {
  return typeof value === 'string' ? value.trim().toUpperCase() : value;
}

function looksLikeLegacyStockDarkPalette(palette) {
  if (!palette || typeof palette !== 'object') return false;

  const matches = LEGACY_STOCK_SIGNATURE_TOKENS.reduce((count, token) => {
    const migration = DARK_PALETTE_MIGRATIONS[token];
    const current = normaliseHex(palette[token]);
    const isLegacyValue = migration?.from?.some(
      (legacy) => normaliseHex(legacy) === current,
    );
    return count + (isLegacyValue ? 1 : 0);
  }, 0);

  // Require a strong legacy signature rather than a single matching colour.
  // This lets admins intentionally use amber/orange or an old neutral value
  // inside an otherwise customised palette without the app rewriting it.
  return matches >= 6;
}

export function normaliseThemeColors(colors) {
  if (!colors || typeof colors !== 'object') return colors;

  const next = {
    ...colors,
    light: colors.light ? { ...colors.light } : colors.light,
    dark: colors.dark ? { ...colors.dark } : colors.dark,
  };

  if (!next.dark) return next;

  const migrateStockDark = looksLikeLegacyStockDarkPalette(next.dark);

  if (migrateStockDark) {
    Object.entries(DARK_PALETTE_MIGRATIONS).forEach(([token, migration]) => {
      const current = normaliseHex(next.dark[token]);
      if (migration.from.some((legacy) => normaliseHex(legacy) === current)) {
        next.dark[token] = migration.to;
      }
    });

    const darkPrimary = normaliseHex(next.dark['--theme-primary']);
    if (LEGACY_ORANGE_PRIMARY.has(darkPrimary)) {
      next.dark['--theme-primary'] = '#002FA7';
      next.dark['--theme-primary-hover'] = '#3B6FFF';
    }
  }

  return next;
}

function hexToRgb(hex) {
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) return null;
  let value = hex.slice(1);
  if (value.length === 3) value = value.split('').map((char) => char + char).join('');
  if (value.length !== 6) return null;
  const parsed = Number.parseInt(value, 16);
  if (Number.isNaN(parsed)) return null;
  return { r: (parsed >> 16) & 255, g: (parsed >> 8) & 255, b: parsed & 255 };
}

function relativeLuminance({ r, g, b }) {
  const linearise = (channel) => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * linearise(r) + 0.7152 * linearise(g) + 0.0722 * linearise(b);
}

function contrastRatio(foreground, background) {
  const fg = hexToRgb(foreground);
  const bg = hexToRgb(background);
  if (!fg || !bg) return null;
  const fgLum = relativeLuminance(fg);
  const bgLum = relativeLuminance(bg);
  const high = Math.max(fgLum, bgLum);
  const low = Math.min(fgLum, bgLum);
  return (high + 0.05) / (low + 0.05);
}

function choosePrimaryForeground(background) {
  const darkRatio = contrastRatio(PRIMARY_DARK_FOREGROUND, background);
  const lightRatio = contrastRatio(PRIMARY_LIGHT_FOREGROUND, background);
  if (darkRatio == null || lightRatio == null) return PRIMARY_LIGHT_FOREGROUND;
  return darkRatio >= lightRatio ? PRIMARY_DARK_FOREGROUND : PRIMARY_LIGHT_FOREGROUND;
}

function withDerivedPrimaryForegrounds(palette) {
  const next = { ...(palette || {}) };
  next['--theme-primary-foreground'] = choosePrimaryForeground(next['--theme-primary']);
  next['--theme-primary-hover-foreground'] = choosePrimaryForeground(next['--theme-primary-hover']);
  return next;
}

function paletteToCss(palette) {
  if (!palette || typeof palette !== 'object') return '';
  return Object.entries(palette)
    .map(([k, v]) => `  ${k}: ${v};`)
    .join('\n');
}

function buildOverrideCss(colors) {
  const normalised = normaliseThemeColors(colors);
  if (!normalised || (!normalised.light && !normalised.dark)) return '';
  const lightCss = normalised.light ? paletteToCss(withDerivedPrimaryForegrounds(normalised.light)) : '';
  const darkCss = normalised.dark ? paletteToCss(withDerivedPrimaryForegrounds(normalised.dark)) : '';
  const parts = [];
  if (darkCss) {
    // Matches the selector used in index.css for dark mode.
    parts.push(`:root, .dark {\n${darkCss}\n}`);
  }
  if (lightCss) {
    parts.push(`.light {\n${lightCss}\n}`);
  }
  return parts.join('\n\n');
}

function applyToDom(colors) {
  const css = buildOverrideCss(colors);
  let tag = document.getElementById(STYLE_TAG_ID);
  if (!tag) {
    tag = document.createElement('style');
    tag.id = STYLE_TAG_ID;
    // Append at the very end of <head> so it wins the cascade over index.css.
    document.head.appendChild(tag);
  }
  tag.textContent = css;
}

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return normaliseThemeColors(JSON.parse(raw));
  } catch {
    return null;
  }
}

function writeCache(colors) {
  // Use quota-safe helper: if the branding cache or similar has
  // blown out localStorage, this will evict them and retry so theme
  // colours still persist across refreshes.
  safeSetItem(CACHE_KEY, JSON.stringify(normaliseThemeColors(colors)));
}

export function applyCachedThemeColors() {
  const cached = readCache();
  if (cached) applyToDom(cached);
}

export async function refreshThemeColors() {
  try {
    const res = await fetch(`${API_URL}/api/settings/theme-colors`);
    if (!res.ok) return null;
    const data = await res.json();
    const colors = normaliseThemeColors({ light: data.light, dark: data.dark });
    writeCache(colors);
    applyToDom(colors);
    // Let any open admin editor know the fresh values are in
    window.dispatchEvent(new CustomEvent('ccuk:theme-colors-updated', { detail: colors }));
    return colors;
  } catch {
    return null;
  }
}

export function setThemeColorsInMemory(colors) {
  // Used by admin editor for live preview without hitting the API.
  const normalised = normaliseThemeColors(colors);
  writeCache(normalised);
  applyToDom(normalised);
  window.dispatchEvent(new CustomEvent('ccuk:theme-colors-updated', { detail: normalised }));
}
